import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "../lib/api";
import type { PortalTrack, PracticeState, TrackListItem } from "../types";

interface PortalValue {
  tracks: TrackListItem[];
  track: PortalTrack | null;
  practice: PracticeState | null;
  selectedSlug: string;
  loading: boolean;
  error: string | null;
  version: string | null;
  selectTrack: (slug: string) => void;
  refresh: () => Promise<void>;
  refreshPractice: () => Promise<void>;
}

const PortalContext = createContext<PortalValue | null>(null);

export function PortalProvider({ children }: { children: ReactNode }) {
  const [tracks, setTracks] = useState<TrackListItem[]>([]);
  const [track, setTrack] = useState<PortalTrack | null>(null);
  const [practice, setPractice] = useState<PracticeState | null>(null);
  const [selectedSlug, setSelectedSlug] = useState(localStorage.getItem("learning-portal-track") || "");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState<string | null>(null);

  const refreshPractice = useCallback(async () => {
    if (!selectedSlug) return;
    try {
      setPractice(await api.practiceState(selectedSlug));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    }
  }, [selectedSlug]);

  const refresh = useCallback(async () => {
    if (!selectedSlug) return;
    setLoading(true);
    try {
      const [nextTrack, nextPractice, nextVersion] = await Promise.all([
        api.track(selectedSlug),
        api.practiceState(selectedSlug),
        api.version(),
      ]);
      setTrack(nextTrack);
      setPractice(nextPractice);
      setVersion(nextVersion.version);
      setError(nextTrack.warnings.length ? nextTrack.warnings.join("；") : null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setLoading(false);
    }
  }, [selectedSlug]);

  useEffect(() => {
    let active = true;
    api.tracks().then((payload) => {
      if (!active) return;
      setTracks(payload.tracks);
      const preferred = payload.tracks.some((item) => item.slug === selectedSlug)
        ? selectedSlug
        : payload.tracks[0]?.slug || "";
      setSelectedSlug(preferred);
      setVersion(payload.version);
    }).catch((reason) => {
      if (active) setError(reason instanceof Error ? reason.message : String(reason));
      setLoading(false);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!selectedSlug) return;
    localStorage.setItem("learning-portal-track", selectedSlug);
    void refresh();
  }, [selectedSlug, refresh]);

  useEffect(() => {
    const timer = window.setInterval(async () => {
      try {
        const next = await api.version();
        if (version && next.version !== version) await refresh();
      } catch {
        // The current snapshot remains usable while a sync is temporarily unavailable.
      }
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [refresh, version]);

  const value = useMemo<PortalValue>(() => ({
    tracks,
    track,
    practice,
    selectedSlug,
    loading,
    error,
    version,
    selectTrack: setSelectedSlug,
    refresh,
    refreshPractice,
  }), [tracks, track, practice, selectedSlug, loading, error, version, refresh, refreshPractice]);

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>;
}

export function usePortal(): PortalValue {
  const value = useContext(PortalContext);
  if (!value) throw new Error("usePortal must be used inside PortalProvider");
  return value;
}
